// The ONE place that decides how a medication dose reads on screen — what drug
// it is, how much, and what actually happened to it.
//
// Every medication surface (patient record, unit-wide record, worklist) derives
// from here. They used to each derive their own, which is how they came to
// disagree: both mapped `status === "completed"` straight to "given" and threw
// away the outcome the nurse had actually recorded, so a dose the patient
// REFUSED displayed as given. That is a patient-safety defect, not a cosmetic
// one — keep this module the single source and they cannot drift apart again.

// The outcome lives in the fulfillment form's `administration_status` field.
// Seeded options (service_fulfillment_type "Medication Administration"):
//   Given | Withheld | Refused by Patient | Patient Vomited After
// A tenant may seed different labels, so match loosely on the recorded string
// rather than assuming this exact vocabulary.
const GIVEN = { key: "given", label: "given", color: "success", variant: "filled" };

const OUTCOME_RULES = [
  [/^\s*(given|administered)\b/i, GIVEN],
  [/refus/i, { key: "refused", label: "refused", color: "error", variant: "outlined" }],
  [/vomit/i, { key: "vomited", label: "vomited after", color: "error", variant: "filled" }],
  [/withh|held|omit/i, { key: "withheld", label: "withheld", color: "warning", variant: "filled" }],
];

// Non-terminal / lifecycle states, derived from the task row itself.
const DUE = { key: "due", label: "due", color: "warning", variant: "outlined" };
const SCHEDULED = { key: "scheduled", label: "scheduled", color: "default", variant: "outlined" };
const CANCELLED = { key: "cancelled", label: "cancelled", color: "default", variant: "outlined" };
const MISSED = { key: "missed", label: "missed", color: "error", variant: "filled" };

// What happened to this dose. Returns { key, label, color, variant, recorded }
// where `recorded` marks a terminal state a nurse actually filed.
function getRecordedOutcome(data) {
  if (!data || typeof data !== "object") return null;
  if (data.administration_status) return data.administration_status;
  for (const [key, val] of Object.entries(data)) {
    if (/status|outcome/i.test(key) && typeof val === "string" && val.trim()) {
      return val;
    }
    if (val && typeof val === "object" && !Array.isArray(val)) {
      const nested = getRecordedOutcome(val);
      if (nested) return nested;
    }
  }
  return null;
}

export function outcomeOf(task, now = new Date()) {
  const status = String(task?.status || "").toLowerCase();
  if (status === "cancelled") return { ...CANCELLED, recorded: false };
  if (status === "missed") return { ...MISSED, recorded: false };

  if (status === "completed") {
    const recordedOutcome = getRecordedOutcome(task?.data);
    if (recordedOutcome) {
      const hit = OUTCOME_RULES.find(([re]) => re.test(recordedOutcome));
      if (hit) return { ...hit[1], recorded: true };
      return { key: "other", label: String(recordedOutcome).toLowerCase(), color: "warning", variant: "outlined", recorded: true };
    }
    return { ...GIVEN, recorded: true };
  }

  const scheduled = task?.scheduled_datetime ? new Date(task.scheduled_datetime) : null;
  const isDue = scheduled && !Number.isNaN(scheduled.getTime()) && scheduled <= now;
  return { ...(isDue ? DUE : SCHEDULED), recorded: false };
}

// True when this dose is still waiting to be recorded — the thing a Give
// action should be offered on.
export const isGiveable = (task, now = new Date()) => {
  const key = outcomeOf(task, now).key;
  return key === "due" || key === "scheduled";
};

// ---------------------------------------------------------------- what & how much

// Where the drug name lives depends on how the order was placed, and all three
// shapes are live in the data:
//   - `order_details.medication.drug_name` — care-plan orders (the documented shape)
//   - `order_details.medication.name`      — voice-assistant / doctor-console orders
//   - `prescription_medicine_name`         — direct orders, which have NO care plan
//     behind them at all (order_details is null) and so are named off the order book
// Without the second and third fallbacks the great majority of doses render as
// the literal word "Medication", which is what made the eMAR unreadable.
export function medicationOf(task) {
  const med = task?.order_details?.medication || {};
  const rx = task?.prescription_details || {};
  return {
    name: med.drug_name || med.name || task?.prescription_medicine_name || "",
    route: med.route || rx.route || "",
    frequency: med.frequency || rx.frequency_code || "",
  };
}

// The drug as it should head a row. Falls back to the task type so a row is
// never blank, but that fallback now fires for genuinely unnamed work only.
export const drugLabel = (task) => medicationOf(task).name || task?.task_type || "Medication";

// Cosmetic unit inference from the product name — "Dolo 650 Tablet" is 1 tab,
// not "1 dose". Presentation only: nothing is stored from this, and an
// unrecognised form falls back to the neutral "dose".
const UNIT_HINTS = [
  [/\b(tablets?|tabs?)\b/i, "tab"],
  [/\b(capsules?|caps?)\b/i, "cap"],
  [/\b(syrup|suspension|solution|drops?|elixir)\b/i, "ml"],
];

const unitFor = (name) => {
  const hit = UNIT_HINTS.find(([re]) => re.test(name || ""));
  return hit ? hit[1] : "dose";
};

// How much, for display. A recorded dose shows what the nurse actually gave
// (`dose_given` IS the clinical record, and the ordered count is overwritten on
// completion). A pending dose shows what was ordered for that round.
export function doseLabel(task) {
  const { name } = medicationOf(task);
  if (String(task?.status || "").toLowerCase() === "completed") {
    const given = task?.data?.dose_given;
    if (given !== undefined && given !== null && String(given).trim() !== "") return String(given);
  }
  const count = task?.data?.slot_dose;
  if (count === undefined || count === null || count === "") return "";
  const unit = unitFor(name);
  return `${count} ${unit}${Number(count) === 1 ? "" : "s"}`;
}

// "1 tab · Oral" — the dose column of every medication table.
export const doseRouteLabel = (task) => {
  const { route } = medicationOf(task);
  return [doseLabel(task), route].filter(Boolean).join(" · ") || "—";
};

// A medication dose, as opposed to a dressing/cannulation/specimen task.
export const isMedicationTask = (task) =>
  Boolean(medicationOf(task).name) || String(task?.task_type || "").toLowerCase() === "medication";

// ------------------------------------------------------- where a dose came from

// Not a field of the seeded fulfillment form — an extra key the app writes into
// the record. It used to be MANDATORY whenever the order was not marked
// Received, which meant every dose, because nothing ever marks an order
// Received. Now it is optional and always offered: "given from ward stock" is a
// legitimate, auditable fact worth capturing, but it must never block a dose.
export const PRE_RECEIPT_SOURCE_KEY = "pre_receipt_source";
export const PRE_RECEIPT_SOURCES = [
  { value: "ward_stock", label: "Ward stock" },
  { value: "emergency_trolley", label: "Emergency trolley" },
  { value: "patient_own", label: "Patient's own supply" },
  { value: "other", label: "Other" },
];

// The recorded source of a dose, ready to display, or "" when none was given.
export const sourceLabel = (task) => {
  const raw = task?.data?.[PRE_RECEIPT_SOURCE_KEY];
  if (!raw) return "";
  return PRE_RECEIPT_SOURCES.find((s) => s.value === raw)?.label || String(raw);
};

// ------------------------------------------------------- recording a dose

// The three outcomes a nurse chooses between at the bedside. `match` finds the
// tenant's own wording for each in the form's option list — the seeded options
// are "Given" / "Withheld" / "Refused by Patient", but a tenant may word them
// differently and we must submit THEIR string, not ours.
export const GIVE_ACTIONS = [
  { key: "given", label: "Confirm", match: /^\s*(given|administered)/i, primary: true },
  { key: "withheld", label: "Withheld", match: /withh|held|omit/i },
  { key: "refused", label: "Refused", match: /refus/i },
];

const optionMatching = (field, re, fallbackIndex = 0) => {
  const options = field?.options || [];
  return options.find((o) => re.test(o)) || options[fallbackIndex] || "";
};

// Field-name heuristics. Names are tenant-seeded, so match on intent rather
// than on an exact key — and anything we fail to recognise is simply left
// empty, which makes the caller fall back to showing the form. Guessing wrong
// is safe; guessing silently would not be.
const IS_TIME = /administer|given|recorded|performed/i;
const IS_DOSE = /dose|quantity|amount/i;
const IS_OUTCOME = /status|outcome/i;
const IS_REACTION = /reaction|adverse/i;

// Everything the sheet can fill in without asking. Returns a values object in
// the same shape initialValues() produces, ready for serialize().
//
// `dose_given` is built from what is already known — the per-round count on the
// task and the medicine name, which carries the strength for most products
// ("1 × Dolo 650 Tablet"). That is why ordering needs no separate dose field.
export function deriveFulfillmentData(task, fields, outcomeKey = "given", now = new Date()) {
  const action = GIVE_ACTIONS.find((a) => a.key === outcomeKey) || GIVE_ACTIONS[0];
  const { name } = medicationOf(task);
  const count = task?.data?.slot_dose;
  const doseText = [count, name].filter((v) => v !== undefined && v !== null && v !== "").join(" × ");

  const values = {};
  fields.forEach((f) => {
    const key = String(f.field || "");
    if (f.type === "object") {
      values[key] = deriveFulfillmentData(task, f.structure || [], outcomeKey, now);
      return;
    }
    if (f.type === "datetime" && IS_TIME.test(key)) {
      const pad = (n) => String(n).padStart(2, "0");
      values[key] = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}`;
      return;
    }
    if (f.type === "select" && IS_OUTCOME.test(key)) {
      values[key] = optionMatching(f, action.match);
      return;
    }
    if (f.type === "select" && IS_REACTION.test(key)) {
      values[key] = optionMatching(f, /^\s*(none|nil|no)\b/i);
      return;
    }
    if (IS_DOSE.test(key) && f.type !== "select") {
      values[key] = doseText;
      return;
    }
    values[key] = "";
  });
  return values;
}
